import type { CSSProperties } from "react";
import type { TimelineItem } from "./items";
import { type Range, type Zoom, barFor, nowPct, packLanes, slotsFor } from "./timeScale";

/**
 * The scaled views (day / week / month / quarter / year), drawn vertically: time runs
 * top to bottom, slot labels sit in a left gutter, and each lane of non-overlapping
 * items is a column. The geometry is the same engine the horizontal version used —
 * an item's position along the axis is just `top`/`height` here rather than
 * `left`/`width`.
 */

/** How tall the axis is per zoom, in px. A day needs room for its 48 half-hours; a
 * year does not need 48 rows of anything. */
const AXIS_HEIGHT: Record<Exclude<Zoom, "list">, number> = {
  day: 960,
  week: 620,
  month: 900,
  quarter: 620,
  year: 560,
};

export function TimelineScale({
  zoom,
  range,
  items,
}: {
  zoom: Zoom;
  range: Range;
  items: TimelineItem[];
}) {
  if (zoom === "list") return null;

  const slots = slotsFor(zoom, range);
  // Three layers, because the marks answer three different questions:
  //  - a schedule occurrence is the time *budgeted* for an activity — background;
  //  - a task (planned) and its actual run are the *work* — lanes;
  //  - an instant has no duration at all — a marker on its own rail.
  // Mixing them made a single long task push every reading into a second lane.
  const budget = items.filter((item) => item.kind === "schedule");
  const spans = items.filter(
    (item) => item.kind !== "schedule" && item.endMs > item.startMs,
  );
  const instants = items.filter((item) => item.endMs === item.startMs);
  const lanes = packLanes(spans, range);
  const today = nowPct(range, Date.now());
  const height = { height: `${AXIS_HEIGHT[zoom]}px` } as CSSProperties;

  return (
    <div className="tlv" style={height}>
      {/* Left gutter: the time labels. */}
      <div className="tlv-gutter">
        {slots.map((slot) => (
          <span
            key={slot.key}
            className="tlv-tick"
            style={{ top: `${slot.leftPct}%`, height: `${slot.widthPct}%` }}
          >
            {slot.label}
          </span>
        ))}
      </div>

      <div className="tlv-plot">
        {/* Slot lines run across every lane, so all columns share one time axis. */}
        {slots.map((slot) => (
          <span key={slot.key} className="tlv-rule" style={{ top: `${slot.leftPct}%` }} />
        ))}

        {/* The scheduled time budget, behind the work — it is the shape of the week
         * an activity intends, not something that was done. */}
        {budget.map((item) => {
          const bar = barFor(range, item);
          if (bar === null) return null;
          const style = {
            top: `${bar.leftPct}%`,
            height: `${bar.widthPct}%`,
            "--item-tint": item.colorHex ?? "var(--a8-text-muted)",
          } as CSSProperties;
          return (
            <span
              key={item.id}
              className="tlv-budget"
              style={style}
              title={`${item.title} (scheduled) — ${formatSpan(item)}`}
            >
              <span className="tlv-budget-label">{item.title}</span>
            </span>
          );
        })}
        {today !== null && (
          <span className="tlv-now" style={{ top: `${today}%` }} aria-hidden="true" />
        )}

        {/* Instants (events, metric readings) ride a rail of their own, against the
         * same axis — they mark a moment rather than occupying a span of it. */}
        <div className="tlv-rail">
          {instants.map((item) => (
            <Marker key={item.id} item={item} range={range} />
          ))}
        </div>

        {lanes.length === 0 && instants.length === 0 ? (
          <p className="tlv-empty">Nothing scheduled in this window.</p>
        ) : (
          <div className="tlv-lanes">
            {lanes.map((lane, index) => (
              <div key={index} className="tlv-lane">
                {lane.map((item) => (
                  <ItemBar key={item.id} item={item} range={range} />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** A moment on the axis: an event or a metric reading. */
function Marker({ item, range }: { item: TimelineItem; range: Range }) {
  const bar = barFor(range, item);
  if (bar === null) return null;

  const style = {
    top: `${bar.leftPct}%`,
    "--item-tint": item.colorHex ?? "var(--a8-text-muted)",
  } as CSSProperties;

  return (
    <span
      className="tlv-marker"
      data-kind={item.kind}
      style={style}
      title={`${item.title} — ${formatSpan(item)}`}
    />
  );
}

/** A span on the axis: a task with a planned start and end. */
function ItemBar({ item, range }: { item: TimelineItem; range: Range }) {
  const bar = barFor(range, item);
  if (bar === null) return null;

  const style = {
    top: `${bar.leftPct}%`,
    height: `${bar.widthPct}%`,
    "--item-tint": item.colorHex ?? "var(--a8-text-muted)",
  } as CSSProperties;

  return (
    <span
      className="tlv-item"
      data-kind={item.kind}
      data-status={item.status}
      style={style}
      title={`${item.title} — ${formatSpan(item)}`}
    >
      <span className="tlv-item-label">{item.title}</span>
    </span>
  );
}

function formatSpan(item: TimelineItem): string {
  const start = new Date(item.startMs).toLocaleString();
  if (item.startMs === item.endMs) return start;
  return `${start} → ${new Date(item.endMs).toLocaleString()}`;
}
